import { useCallback, useRef } from 'react';
import gettext from '@sdk/lang/gettext';
import Modal from '@sdk/components/ui/modal/Modal';
import InnerModal from '@sdk/components/ui/confirmModal/InnerModal';
import Row from '@sdk/components/ui/spacing/Row';
import UserIconSvg from '@sdk/assets/svg/user-blue.svg';
import EditablePicker from '@sdk/components/forms/EditablePicker';

import { useMergeModal } from './mergeModal.hooks';
import { SectionHeader, LeftColumn, RightColumn } from './mergeModal.styles';
import Confirmation from './Confirmation';
import Description from './Description';
import FromSection from './FromSection';
import type { MergeModalItem } from './mergeModal.types';

type Props = {
  isOpen: boolean;
  fromItems: MergeModalItem[];
  toItems: MergeModalItem[];
  onClose: () => void;
  onSubmit: (fromItems: MergeModalItem[], targetItem: MergeModalItem) => Promise<any>;
  getModalTitle?: (isMultiple: boolean) => string;
  getHeader?: (isMultiple: boolean) => string;
  getDescription?: (isMultiple: boolean) => string;
  getConfirmation?: (isMultiple: boolean) => string;
  iconComponent?: () => JSX.Element;
  pickerItemComponent?: (item: MergeModalItem) => JSX.Element;
  placeholder?: string;
  pickerInvalidStateText?: string;
};

const MergeModal = ({
  isOpen,
  fromItems,
  toItems,
  onClose,
  onSubmit,
  getModalTitle = (isMultiple) => (isMultiple ? gettext('Merge items') : gettext('Merge item')),
  getHeader = (isMultiple) => (isMultiple ? gettext('Selected items') : gettext('Selected item')),
  getDescription = (isMultiple) =>
    isMultiple
      ? gettext('Please specify which item the selected items need to be merged into:')
      : gettext('Please specify which item the selected item needs to be merged into:'),
  getConfirmation = (isMultiple) =>
    isMultiple
      ? gettext('[[items]] and [[lastItem]] will be merged into [[target]]')
      : gettext('[[lastItem]] will be merged into [[target]]'),
  iconComponent = UserIconSvg,
  pickerItemComponent,
  placeholder,
  pickerInvalidStateText,
}: Props) => {
  const {
    isConfirmation,
    isMultiple,
    isVisible,
    onDismiss,
    onMergeClick,
    pickerProps,
    targetItemId,
  } = useMergeModal({
    isOpen,
    fromItems,
    toItems,
    onClose,
    onSubmit,
    placeholder,
    pickerInvalidStateText,
  });
  const IconComponent = iconComponent;
  const config = useRef({ hideClose: true }).current;
  const ModalBody = useCallback(
    () =>
      isMultiple ? (
        <>
          <FromSection
            fromItems={fromItems}
            horizontal={true}
            header={getHeader(isMultiple)}
            itemComponent={pickerItemComponent}
          />
          <Description>{getDescription(isMultiple)}</Description>
          <EditablePicker {...pickerProps} useOptionsFiltering renderItem={pickerItemComponent} />
        </>
      ) : (
        <>
          <Description>{getDescription(isMultiple)}</Description>
          <Row>
            <LeftColumn>
              <FromSection
                fromItems={fromItems}
                header={getHeader(isMultiple)}
                itemComponent={pickerItemComponent}
              />
            </LeftColumn>
            <RightColumn>
              <SectionHeader>{gettext('Merging into')}</SectionHeader>
              <EditablePicker
                {...pickerProps}
                useOptionsFiltering
                renderItem={pickerItemComponent}
              />
            </RightColumn>
          </Row>
        </>
      ),
    [getDescription, getHeader, isMultiple, fromItems, pickerProps, pickerItemComponent]
  );

  return (
    <Modal fixed isVisible={isVisible} config={config}>
      <InnerModal
        titleText={getModalTitle(isMultiple)}
        titleIcon={<IconComponent />}
        primaryButtonText={gettext('Merge')}
        primaryButtonDisabled={!targetItemId}
        primaryButtonWithProgress={isConfirmation}
        onPrimaryClick={onMergeClick}
        secondaryButtonText={gettext('Cancel')}
        onSecondaryClick={onDismiss}
        dismiss={onDismiss}
        dismissOnSuccess={isConfirmation}>
        {isConfirmation ? (
          <Confirmation
            fromItems={fromItems}
            targetItem={toItems?.find((item) => item.id === targetItemId)}>
            {getConfirmation(isMultiple)}
          </Confirmation>
        ) : (
          <ModalBody />
        )}
      </InnerModal>
    </Modal>
  );
};

export default MergeModal;
